import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, CheckCircle2, Clock3, CalendarDays } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const localISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const fmtDay = (iso) =>
  new Date(iso + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

const BookingModal = () => {
  const [open, setOpen] = useState(false);
  const [types, setTypes] = useState([]);
  const [typeId, setTypeId] = useState("free-consultation");
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState(null);
  const [time, setTime] = useState("");
  const [form, setForm] = useState({ name: "", email: "", note: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    const openHandler = () => setOpen(true);
    window.addEventListener("open-booking", openHandler);
    axios.get(`${API}/bookings/session-types`).then((r) => setTypes(r.data)).catch(() => {});
    return () => window.removeEventListener("open-booking", openHandler);
  }, []);

  const type = types.find((t) => t.id === typeId);

  const dateOptions = [];
  if (type) {
    for (let i = 1; i <= 28 && dateOptions.length < 8; i++) {
      const d = new Date();
      d.setDate(d.getDate() + i);
      if (type.days.includes(d.getDay())) dateOptions.push(localISO(d));
    }
  }

  useEffect(() => {
    if (!date || !typeId) return;
    setSlots(null);
    setTime("");
    axios
      .get(`${API}/bookings/slots`, { params: { session_type: typeId, date } })
      .then((r) => setSlots(r.data.slots))
      .catch(() => setSlots([]));
  }, [date, typeId]);

  const close = () => {
    setOpen(false);
    setDone(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/bookings`, {
        name: form.name,
        email: form.email,
        session_type: typeId,
        date,
        time,
        note: form.note || null,
      });
      setDone(data.booking);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not book this slot", { description: "Try another time slot." });
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#ffffff] border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder:text-slate-500 focus:outline-none focus:border-amber-400/60 transition-colors";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
          onClick={close}
          data-testid="booking-modal-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 32, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg rounded-3xl border border-slate-200 bg-[#F8FAFC] p-7 shadow-2xl max-h-[88vh] overflow-y-auto"
            data-testid="booking-modal"
          >
            <div className="flex items-start justify-between mb-5">
              <div>
                <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-amber-600 mb-1">Book a Session</p>
                <h3 className="font-display text-xl font-bold tracking-tight">Pick a slot with Vikram</h3>
              </div>
              <button onClick={close} className="p-1.5 rounded-full hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition-colors" data-testid="booking-modal-close" aria-label="Close">
                <X size={16} />
              </button>
            </div>

            {done ? (
              <div className="text-center py-8" data-testid="booking-success">
                <CheckCircle2 size={44} className="mx-auto text-emerald-600 mb-5" />
                <h4 className="font-display text-lg font-bold mb-2">Session booked</h4>
                <p className="text-sm text-slate-600 mb-1">{done.session_title}</p>
                <p className="font-mono2 text-sm text-amber-600">
                  {fmtDay(done.date)} • {done.time} IST • {done.minutes} min
                </p>
                <p className="text-xs text-slate-500 mt-4">A confirmation is on its way to {done.email}.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5">
                <div className="grid grid-cols-2 gap-2.5">
                  {types.map((t) => (
                    <button
                      type="button"
                      key={t.id}
                      onClick={() => {
                        setTypeId(t.id);
                        setDate("");
                        setSlots(null);
                      }}
                      className={`text-left rounded-2xl border p-3.5 transition-all duration-300 ${
                        typeId === t.id ? "border-amber-400/60 bg-amber-400/10" : "border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid={`booking-type-${t.id}`}
                    >
                      <div className="text-[13px] font-semibold text-slate-900 leading-snug">{t.title}</div>
                      <div className="font-mono2 text-[10px] text-slate-500 mt-1.5 flex items-center gap-1">
                        <Clock3 size={10} /> {t.minutes} min • {t.price}
                      </div>
                    </button>
                  ))}
                </div>
                {type && <p className="text-xs text-slate-500 -mt-2">{type.description}</p>}

                <div>
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2.5 flex items-center gap-1.5">
                    <CalendarDays size={11} /> Date (IST)
                  </p>
                  <div className="flex flex-wrap gap-2" data-testid="booking-date-options">
                    {dateOptions.map((d) => (
                      <button
                        type="button"
                        key={d}
                        onClick={() => setDate(d)}
                        className={`font-mono2 text-[11px] px-3.5 py-2 rounded-full border transition-all ${
                          date === d ? "bg-amber-400 text-slate-900 border-amber-400 font-bold" : "border-slate-200 text-slate-600 hover:border-amber-400/50"
                        }`}
                        data-testid={`booking-date-${d}`}
                      >
                        {fmtDay(d)}
                      </button>
                    ))}
                  </div>
                </div>

                {date && (
                  <div>
                    <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2.5">Time (IST)</p>
                    {slots === null ? (
                      <Loader2 size={16} className="animate-spin text-amber-600" />
                    ) : slots.length === 0 ? (
                      <p className="text-xs text-slate-500">No slots left on this date — pick another.</p>
                    ) : (
                      <div className="flex flex-wrap gap-2" data-testid="booking-slot-options">
                        {slots.map((s) => (
                          <button
                            type="button"
                            key={s}
                            onClick={() => setTime(s)}
                            className={`font-mono2 text-[11px] px-3.5 py-2 rounded-full border transition-all ${
                              time === s ? "bg-amber-400 text-slate-900 border-amber-400 font-bold" : "border-slate-200 text-slate-600 hover:border-amber-400/50"
                            }`}
                            data-testid={`booking-slot-${s.replace(":", "")}`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {time && (
                  <div className="space-y-3 pt-1">
                    <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className={inputCls} data-testid="booking-form-name-input" />
                    <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email address" className={inputCls} data-testid="booking-form-email-input" />
                    <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="What would you like to discuss? (optional)" className={inputCls} data-testid="booking-form-note-input" />
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full flex items-center justify-center gap-2 bg-amber-400 text-slate-900 font-semibold text-sm px-6 py-3.5 rounded-full hover:bg-amber-500 disabled:opacity-60 transition-colors"
                      data-testid="booking-form-submit-button"
                    >
                      {loading && <Loader2 size={15} className="animate-spin" />}
                      {loading ? "Booking..." : `Confirm ${fmtDay(date)} • ${time} IST`}
                    </button>
                  </div>
                )}
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BookingModal;
